import { useCallback, useEffect, useState } from 'react';
import { authHeaders, axiosInstance, isEmptyData, sendAnalyticsEvent } from '../../utils/helper';
import useToast from '../Toast';
import { LoaderIcon } from '../../utils/svg';
import { useSelector, useDispatch } from 'react-redux';
import { getOrderTypeOptions, setOrder, tableNumberAuto } from '../../store/reducers/order';
import { cleanText } from '../../utils/helper';
import useNavigation from 'hook/useNavigation';
import { handleGetStoreDetail } from 'store/reducers/outlet';
import { BottomSheet } from 'react-spring-bottom-sheet';
import Confirmation from 'ufood-x/components/Header/Confirmation';
import { removeCart, handleGetCartDetail } from 'store/reducers/cart';
import { deliveryId, uniqueID } from 'utils/constants';

const active_class = 'text-white bg-[var(--rose-violet)] border-[var(--rose-violet)]';
const inactive_class = 'text-gray-400 bg-gray-100 border-gray-100';

const nameMap = {
	delivery: 'Delivery',
	pickup: 'Self Pick up',
	dinein: 'Dine in',
};

const OrderOption = ({ 
		isDisabled = false, 
		setValues = () => {}, 
		setDefaultValues = () => {}, 
		outletId = null, 
		isShowConfirmation = false,
		setIsShowConfirmation = () => {},
	}) => {
	const { showToast } = useToast();
	const dispatch = useDispatch();
	const navigate = useNavigation();
	const [isLoading, setIsLoading] = useState(false);
	const [active, setActive] = useState('delivery');
	const [orderTypeList, setOrderTypeList] = useState([]);
  	const user = useSelector((store) => store?.user);
	const { orderTypeOptions, selectedOrderType, tableNumber} = useSelector((store) => store?.order);
	const { details: outlet } = useSelector((store) => store.outlet);
	const [pendingType, setPendingType] = useState(null);
	const [cart, setCart] = useState(null)

	const handleCheckProductAvability = (type) => {
		if (isEmptyData(cart?.item)) return true;

		const typeClean = cleanText(type);

		const isAllAllowed = cart.item.every((cartItem) => {
			if (isEmptyData(cartItem?.product_availability)) return true;

			return cartItem.product_availability.some((availability) => cleanText(availability?.av_name) === typeClean);
		});

		return isAllAllowed;
	};

	const handleClick = (type = null) => {
		if (!type) return;
    	sendAnalyticsEvent('Select Order Type', 'summary_select_order_type', type?.label);
		const isAllowed = handleCheckProductAvability(cleanText(type?.av_name));
		if (
			!isAllowed
			|| (cleanText(type?.av_name) == 'dinein' && !tableNumber?.table_number)
		) {
			setPendingType(type);
			setIsShowConfirmation(true);
			return;
		}

		setActive(type?.label);
		setValues((prev) => {
			return {
				...prev,
				orderType: type?.label,
				availabilityID: type?.av_id,
				availabilityName: type?.av_name,
			};
		});

		const selectedOrderType = orderTypeOptions.find((t) => cleanText(t?.av_name) === cleanText(type?.av_name));
		dispatch(
			setOrder({
				selectedOrderType: selectedOrderType,
			}),
		);
	};

	const handleGetCart = useCallback(async () => {
			try {
				setIsLoading(true);
				const url = `cart/loadCartDetails?unquieid=${uniqueID}&availabilityID=${deliveryId}&storeID=${outletId}`;
				const res = await axiosInstance.get(url, {
					headers: {
						Accept: 'application/json, text/plain, */*',
						...authHeaders().headers,
					},
				});
				if (res?.data?.status === 'ok') {
					setCart(res?.data?.result || null);
				}
				if (res?.data?.message === 'There are no items in your cart') {
					setCart(null);
				}
				setIsLoading(false);
			} catch (error) {
				console.log('error: ', error);
				setIsLoading(false);
			}
		}, [outletId]);

	const handleGetOutlet = async () => {
		dispatch(
			handleGetStoreDetail({
				storeID: outletId,
			}),
		);
	};

	const handleConfirm = async () => {
		if (!pendingType) return;
	
		try {
		  await dispatch(removeCart()).unwrap();
	
		  const selectedOrderType = orderTypeOptions.find((t) => cleanText(t?.av_name) === cleanText(pendingType?.av_name));
		  dispatch(setOrder({ selectedOrderType }));
	
		  navigate.push(`outlet/${outlet?.storeSlug}/${cleanText(pendingType?.av_name)}`);
		  handleCloseBottomSheet();
		} catch (error) {
		  console.log('error: ', error);
		}
	  };
	
	const handleCancel = () => {
		handleCloseBottomSheet();
	};

	const handleCloseBottomSheet = () => {
		setIsShowConfirmation(false);
	};

	useEffect(() => {
		const orderTypeList = orderTypeOptions?.map((d) => {
			const name = d?.av_name?.toLowerCase();
			if (cleanText(name) === 'dinein') return null;
			return {
				...d,
				label: name,
				name: nameMap[name] || d?.av_name || '',
			};
		}).filter(Boolean);

		setOrderTypeList(orderTypeList);

		const orderType = selectedOrderType || orderTypeOptions?.[0]; 

		setDefaultValues((prev) => {
            return {
				...prev,
				orderType: orderType?.av_name?.toLowerCase(),
				availabilityID: orderType?.av_id,
				availabilityName: orderType?.av_name,
            };
        });

		handleGetOutlet();
	}, [orderTypeOptions]);

	useEffect(() => {
		handleGetCart();
		setActive(selectedOrderType?.av_name?.toLowerCase());
	}, [])

	return (
		<>
			<div className={`p-4 flex justify-between bg-white gap-4 border-b-1 border-solid pb-4 border-gray-200 ${orderTypeList?.length === 1 && 'hidden'}`}>
				{isLoading ? (
					<div className='z-[1] w-full flex items-center justify-center'>
						<LoaderIcon />
					</div>
				) : (
					orderTypeList?.map((type, index) => {
						return (
							<button
								key={index}
								type='button'
								disabled={isDisabled}
								onClick={() => handleClick(type)}
								className={`w-full px-2 py-2 rounded-full text-sm font-medium disabled:bg-gray-300 disabled:text-white disabled:border-gray-100 ${active === type?.label ? active_class : inactive_class}`}>
								{type?.name}
							</button>
						);
					})
				)}
			</div>

			<BottomSheet key={'order-type-options'} open={isShowConfirmation} onDismiss={handleCloseBottomSheet}>
				<Confirmation onConfirm={handleConfirm} onCancel={handleCancel} />
			</BottomSheet>
		</>
	);
};

export default OrderOption;
